import React from 'react';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Button, Stack } from '@mui/material';
import {
  setUserRobotsAPI,
  deleteUserRobotsAPI,
  getUserRobotsAPI,
  updateUserRobotsAPI,
  getRobotDataAPI,
  updateRobotDataAPI,
  getUserGroupsAPI,
  initRobotAPI,
} from '../api/robot';
import {
  MODAL_TYPE_CONFIG,
  MODAL_TYPE_UPDATE,
  MODAL_TYPE_CALL,
} from '../common/consts';
import { getIndex, getUserId, getUserPId } from '../common/util';
import { piboReset } from '../pibo';

const MODULE_ROBOT = 'robot';

const MANUAL_ITEM_INITIAL = {
  userId: '',
  serials: [],
  error: null,
};

const MODAL_INITIAL = {
  loading: false,
  open: false,
  items: [],
  groups: [],
  bCon: false,
  error: null,
  result: null,
  message: null,
};

const initialState = {
  loading: false,
  error: null,
  contentItems: [],
  list: [],
  total: 0,
  modal: { ...MODAL_INITIAL },
};

const createListData = (d, i) => {
  const { alias, group, id, bot, serial, user } = d;

  const buttons = (
    <Stack direction="row">
      <Button
        name={MODAL_TYPE_UPDATE}
        color="secondary"
        size="small"
        variant="contained"
        sx={{ minWidth: 50, mr: 1 }}
      >
        수정
      </Button>
      <Button
        name={MODAL_TYPE_CONFIG}
        color="secondary"
        size="small"
        variant="contained"
        sx={{ minWidth: 50, mr: 1 }}
      >
        설정
      </Button>
      <Button
        name={MODAL_TYPE_CALL}
        color="secondary"
        size="small"
        variant="contained"
        sx={{ minWidth: 50 }}
      >
        통화
      </Button>
    </Stack>
  );
  const groupName = group && 'name' in group ? group.name : '';
  const item = [
    { key: 'index', content: getIndex(i + 1) },
    { key: 'serial', content: serial },
    { key: 'alias', content: alias },
    { key: 'group', content: groupName },
    { key: 'user', content: `${user} 명` },
    { key: 'bot', content: `${bot} 개` },
    { key: 'buttons', content: buttons },
  ];

  return { id, key: id, item, disabled: false };
};

export const robotInit = createAsyncThunk(
  `${MODULE_ROBOT}/init`,
  async (robotId, { rejectWithValue }) => {
    try {
      const { result, data } = await initRobotAPI({ robotId });
      if (result) {
        const { robotId: serial, geo, alias, reaction, volume } = data;
        piboReset();
        return {
          msg: '로봇이 성공적으로 초기화 되었습니다.',
          data: [
            { id: robotId, serial, geo, alias, reaction, volume, disturb: {} },
          ],
        };
      }
      return rejectWithValue('로봇 초기화에 도중 문제가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('로봇 초기화에 도중 문제가 발생했습니다.');
    }
  },
);

export const getData = createAsyncThunk(
  `${MODULE_ROBOT}/getData`,
  async (conditions, { rejectWithValue }) => {
    try {
      const { result, list, total } = await getUserRobotsAPI({
        ...conditions,
        userId: getUserPId(),
      });
      if (result) {
        if (list.length === 0)
          return rejectWithValue('로봇 목록이 존재하지 않습니다.');
        const contentItems = list.map((item, i) =>
          createListData(item, conditions.desc ? list.length - 1 - i : i),
        );
        return { list, contentItems, total, listLength: 0 };
      }
      return rejectWithValue('로봇 목록 로드 중 에러가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('로봇 목록 로드 중 에러가 발생했습니다.');
    }
  },
);

export const configUpdate = createAsyncThunk(
  `${MODULE_ROBOT}/CONFIG_UPDATE`,
  async ({ robotId, ...updateData }, { rejectWithValue }) => {
    try {
      const [name] = Object.keys(updateData);
      await updateRobotDataAPI({ robotId, updateData });
      switch (name) {
        case 'reaction':
          return '로봇 성향 설정이 성공적으로 저장되었습니다.';
        case 'geo':
          return '로봇 위치 설정이 성공적으로 저장되었습니다.';
        case 'disturb':
          return '로봇 방해금지모드 설정이 성공적으로 저장되었습니다.';
        default:
          return '로봇 설정이 성공적으로 저장되었습니다.';
      }
    } catch (error) {
      return rejectWithValue(
        '설정 저장 중 오류가 발생했습니다.\n나중에 다시 시도하세요.',
      );
    }
  },
);

export const updateItems = createAsyncThunk(
  `${MODULE_ROBOT}/UPDATE`,
  async ({ list: robots, groups }, { rejectWithValue }) => {
    try {
      const result = await updateUserRobotsAPI({
        userId: getUserPId(),
        robots,
        groups,
      });
      if (result) {
        return `${robots.length}개의 로봇 정보가 수정되었습니다.`;
      }
      return rejectWithValue(`로봇 정보 수정 도중 에러가 발생했습니다.`);
    } catch (error) {
      return rejectWithValue(
        `로봇 정보 수정 도중 에러가 발생했습니다. ${error}`,
      );
    }
  },
);

export const openModal = createAsyncThunk(
  `${MODULE_ROBOT}/openModal`,
  async ({ items, type }, { rejectWithValue }) => {
    if (type !== MODAL_TYPE_CALL) {
      const bCon = type === MODAL_TYPE_CONFIG;
      if (bCon) {
        const { id: robotId, alias } = items[0];
        const {
          result,
          list: data,
          error,
        } = await getRobotDataAPI({ robotId });

        if (!result || error) {
          return rejectWithValue(
            '로봇 설정을 위한 정보 조회 중 오류가 발생했습니다.\n나중에 다시 시도하세요.',
          );
        }
        const { serial, geo, disturb, reaction, volume } = data[0];
        return {
          open: true,
          bCon,
          items: [
            { id: robotId, serial, geo, disturb, alias, reaction, volume },
          ],
        };
      }
      const { result, groups } = await getUserGroupsAPI({
        userId: getUserPId(),
      });
      if (result) {
        return {
          open: true,
          bCon,
          items,
          groups,
        };
      }
      return rejectWithValue(
        '로봇 정보 수정을 위한 정보 조회 중 오류가 발생했습니다.\n나중에 다시 시도하세요.',
      );
    }
    const { serial } = items[0];
    return {
      open: true,
      bCall: true,
      items: { caller: getUserId(), callee: serial },
    };
  },
);

/* 로봇 등록 결과값으로 modal 띄워줘야함 */
export const onDirectSaveItem = createAsyncThunk(
  `${MODULE_ROBOT}/SAVE`,
  async ({ userId, serials }, { rejectWithValue }) => {
    const robots = serials.reduce((acc, cur) => {
      acc.push({ serial: cur });
      return acc;
    }, []);
    try {
      const { result, robotIds, error } = await setUserRobotsAPI({
        userId,
        robots,
      });
      if (result) {
        return robotIds;
      }
      return error.desc;
    } catch (error) {
      return rejectWithValue('로봇 등록 중 에러가 발생했습니다.');
    }
  },
);

/* 로봇 제거 결과값으로 modal 띄워줘야함 */
export const onDirectRemoveItem = createAsyncThunk(
  `${MODULE_ROBOT}/REMOVE`,
  async ({ userId, serials }, { rejectWithValue }) => {
    try {
      const robots = serials.reduce((acc, cur) => {
        acc.push({ serial: cur });
        return acc;
      }, []);
      const { result, robotIds, error } = await deleteUserRobotsAPI({
        userId,
        robots,
      });
      if (result) {
        return robotIds;
      }
      return error.desc;
    } catch (error) {
      return rejectWithValue('로봇 삭제 중 에러가 발생했습니다.');
    }
  },
);

const robotSlice = createSlice({
  name: MODULE_ROBOT,
  initialState,
  reducers: {
    init: () => ({ ...initialState }),
    closeModal: (state) => ({
      ...state,
      modal: { ...MODAL_INITIAL },
    }),
    initRegisterItem: (state) => ({
      ...state,
      registerItem: { ...MANUAL_ITEM_INITIAL },
    }),
    updateRegisterItem: (state, action) => ({
      ...state,
      registerItem: action.payload,
    }),
    updateRemoveItem: (state, action) => ({
      ...state,
      removeItem: action.payload,
    }),
    initRemoveItem: (state) => ({
      ...state,
      removeItem: { ...MANUAL_ITEM_INITIAL },
    }),
  },
  extraReducers: {
    [getData.pending.type]: (state) => ({ ...state, loading: true }),
    [getData.fulfilled.type]: (state, action) => ({
      ...state,
      loading: false,
      ...action.payload,
    }),
    [getData.rejected.type]: (state, action) => ({
      ...state,
      loading: false,
      error: action.payload,
    }),
    [openModal.pending.type]: (state) => ({
      ...state,
      modal: { ...MODAL_INITIAL, loading: true },
    }),
    [openModal.fulfilled.type]: (state, action) => ({
      ...state,
      modal: { ...state.modal, loading: false, ...action.payload },
    }),
    [onDirectRemoveItem.pending.type]: (state) => ({
      ...state,
      loading: true,
      error: { ...state.error, remove: null },
      removed: [],
    }),
    [onDirectRemoveItem.fulfilled.type]: (state, action) => ({
      ...state,
      loading: false,
      removed: action.payload,
    }),
    [onDirectRemoveItem.rejected.type]: (state, action) => ({
      ...state,
      loading: false,
      error: { ...state.error, remove: action.payload },
    }),
    [onDirectSaveItem.pending.type]: (state) => ({
      ...state,
      loading: true,
      error: { ...state.error, save: null },
      saved: [],
    }),
    [onDirectSaveItem.fulfilled.type]: (state, action) => ({
      ...state,
      loading: false,
      saved: action.payload,
    }),
    [onDirectSaveItem.rejected.type]: (state, action) => ({
      ...state,
      loading: false,
      error: { ...state.error, save: action.payload },
    }),
    [configUpdate.pending.type]: (state) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: true,
        result: null,
        message: null,
        error: null,
      },
    }),
    [configUpdate.fulfilled.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: false,
        result: true,
        message: action.payload,
      },
    }),
    [configUpdate.rejected.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: false,
        result: false,
        error: action.payload,
      },
    }),
    [updateItems.pending.type]: (state) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: true,
        result: null,
        message: null,
        error: null,
      },
    }),
    [updateItems.fulfilled.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: false,
        result: true,
        message: action.payload,
      },
    }),
    [updateItems.rejected.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: false,
        result: false,
        error: action.payload,
      },
    }),
    [robotInit.pending.type]: (state) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: true,
        result: null,
        message: null,
        error: null,
        init: true,
      },
    }),
    [robotInit.fulfilled.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        items: action.payload.data,
        loading: false,
        result: true,
        message: action.payload.msg,
      },
    }),
    [robotInit.rejected.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: false,
        result: false,
        error: action.payload,
      },
    }),
  },
});

const { reducer: RobotReducer, actions } = robotSlice;
export const {
  init,
  closeModal,
  initRegisterItem,
  updateRegisterItem,
  updateRemoveItem,
  initRemoveItem,
} = actions;

export default RobotReducer;
